import { Howl } from 'howler';

// Casino Audio Management System
class CasinoAudio {
  constructor() {
    this.sounds = {};
    this.volume = 0.3; // Default volume (30%)
    this.enabled = true;
    this.initialized = false;
    
    // Check user preferences
    this.loadUserPreferences();
  }

  // Initialize audio system (call on user interaction)
  init() {
    if (this.initialized) return;
    
    try {
      // Load casino sound effects
      this.sounds.cardFlip = new Howl({
        src: ['/audio/casino/card-flip.mp3', '/audio/casino/card-flip.wav'],
        volume: this.volume,
        preload: true,
        html5: true
      });

      this.sounds.chipClick = new Howl({
        src: ['/audio/casino/chip-click.mp3', '/audio/casino/chip-click.wav'],
        volume: this.volume * 0.8,
        preload: true,
        html5: true
      });

      this.sounds.goldChime = new Howl({
        src: ['/audio/casino/gold-chime.mp3', '/audio/casino/gold-chime.wav'],
        volume: this.volume * 0.6,
        preload: true,
        html5: true
      });

      this.sounds.successFanfare = new Howl({
        src: ['/audio/casino/success-fanfare.mp3', '/audio/casino/success-fanfare.wav'],
        volume: this.volume * 0.7,
        preload: true,
        html5: true
      });

      this.sounds.errorBuzz = new Howl({
        src: ['/audio/casino/error-buzz.mp3', '/audio/casino/error-buzz.wav'],
        volume: this.volume * 0.5,
        preload: true,
        html5: true
      });

      // Ambient casino atmosphere (very low volume)
      this.sounds.ambient = new Howl({
        src: ['/audio/casino/casino-ambient.mp3', '/audio/casino/casino-ambient.wav'],
        volume: this.volume * 0.2,
        loop: true,
        preload: true,
        html5: true
      });

      this.initialized = true;
      
      // Start ambient sound if enabled
      if (this.enabled) {
        this.playAmbient();
      }
    } catch (error) {
      console.warn('Casino audio initialization failed:', error);
      this.enabled = false;
    }
  }

  // Load user audio preferences
  loadUserPreferences() {
    try {
      const preferences = localStorage.getItem('casinoAudioPreferences');
      if (preferences) {
        const prefs = JSON.parse(preferences);
        this.volume = prefs.volume || 0.3;
        this.enabled = prefs.enabled !== false; // Default to true
      }
    } catch (error) {
      console.warn('Failed to load audio preferences:', error);
    }
  }

  // Save user audio preferences
  saveUserPreferences() {
    try {
      const preferences = {
        volume: this.volume,
        enabled: this.enabled
      };
      localStorage.setItem('casinoAudioPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save audio preferences:', error);
    }
  }

  // Play specific sound effect
  play(soundName, options = {}) {
    if (!this.enabled || !this.initialized) return;

    try {
      const sound = this.sounds[soundName];
      if (sound) {
        // Apply any custom volume for this play
        if (options.volume !== undefined) {
          sound.volume(options.volume);
        }
        
        sound.play();

        // Reset volume after playing if it was customized
        if (options.volume !== undefined) {
          setTimeout(() => {
            sound.volume(this.volume * (options.baseVolume || 1));
          }, 100);
        }
      }
    } catch (error) {
      console.warn(`Failed to play sound: ${soundName}`, error);
    }
  }

  // Play ambient casino atmosphere
  playAmbient() {
    if (!this.enabled || !this.initialized) return;
    
    try {
      if (this.sounds.ambient && !this.sounds.ambient.playing()) {
        this.sounds.ambient.play();
      }
    } catch (error) {
      console.warn('Failed to play ambient sound:', error);
    }
  }

  // Stop ambient sound
  stopAmbient() {
    try {
      if (this.sounds.ambient) {
        this.sounds.ambient.stop();
      }
    } catch (error) {
      console.warn('Failed to stop ambient sound:', error);
    }
  }

  // Set master volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    
    if (this.initialized) {
      Object.values(this.sounds).forEach(sound => {
        if (sound && typeof sound.volume === 'function') {
          sound.volume(this.volume);
        }
      });
    }
    
    this.saveUserPreferences();
  }

  // Enable/disable audio
  setEnabled(enabled) {
    this.enabled = enabled;
    
    if (!enabled) {
      this.stopAll();
    } else if (this.initialized) {
      this.playAmbient();
    }
    
    this.saveUserPreferences();
  }

  // Stop all sounds
  stopAll() {
    if (!this.initialized) return;
    
    try {
      Object.values(this.sounds).forEach(sound => {
        if (sound && typeof sound.stop === 'function') {
          sound.stop();
        }
      });
    } catch (error) {
      console.warn('Failed to stop all sounds:', error);
    }
  }

  // Convenience methods for specific interactions
  playCardFlip() {
    this.play('cardFlip', { baseVolume: 1 });
  }

  playChipClick() {
    this.play('chipClick', { baseVolume: 0.8 });
  }

  playGoldChime() {
    this.play('goldChime', { baseVolume: 0.6 });
  }

  playSuccess() {
    this.play('successFanfare', { baseVolume: 0.7 });
  }

  playError() {
    this.play('errorBuzz', { baseVolume: 0.5 });
  }

  // Get current state
  getState() {
    return {
      enabled: this.enabled,
      volume: this.volume,
      initialized: this.initialized,
      isAmbientPlaying: this.sounds.ambient ? this.sounds.ambient.playing() : false
    };
  }
}

// Create singleton instance
const casinoAudio = new CasinoAudio();

// Audio hook for React components
export const useCasinoAudio = () => {
  const initAudio = () => {
    casinoAudio.init();
  };

  const playSound = (soundName, options) => {
    casinoAudio.play(soundName, options);
  };

  const setVolume = (volume) => {
    casinoAudio.setVolume(volume);
  };

  const setEnabled = (enabled) => {
    casinoAudio.setEnabled(enabled);
  };

  const getState = () => {
    return casinoAudio.getState();
  };

  // Convenience methods
  const playCardFlip = () => casinoAudio.playCardFlip();
  const playChipClick = () => casinoAudio.playChipClick();
  const playGoldChime = () => casinoAudio.playGoldChime();
  const playSuccess = () => casinoAudio.playSuccess();
  const playError = () => casinoAudio.playError();

  return {
    initAudio,
    playSound,
    setVolume,
    setEnabled,
    getState,
    playCardFlip,
    playChipClick,
    playGoldChime,
    playSuccess,
    playError
  };
};

export default casinoAudio;